import { useDemoFetcher, useDemoFetcherWithSwr } from "@/api";
import { userAtom } from "@/atoms/userAtom";
import { useAtomValue } from "jotai";
import { useCallback } from "react";

/**
 * ツアー完了状態を管理するカスタムフック。
 * unified-api の user_config（DynamoDB）で状態を管理する。
 *
 * 書き込みは「update（部分更新）を先に試し、レコードが無い（404）ときだけ
 * create する」upsert 方式。GET の成否に依存して create（全置換）へ転ばない
 * ため、ネットワークエラー時に他項目（配信設定など）をデフォルトへ巻き戻す
 * 事故が起きない。
 */
export const useOnboardingTourStatus = () => {
  const user = useAtomValue(userAtom);
  const userId = user?.userId;

  // ユーザーがログイン済みの場合のみDBから取得
  const shouldFetch = !!userId;

  // DBからツアー状態を取得
  const {
    data: userConfig,
    error,
    isLoading,
    mutate
  } = useDemoFetcherWithSwr({
    apiName: "user_config",
    serviceName: "userConfig",
    operationId: "getUserConfig",
    requestParameters: {
      userId: userId || ""
    },
    enabled: shouldFetch,
    swrConfiguration: {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      shouldRetryOnError: false
    }
  });

  // 書き込み用フェッチャ（{ data, error } を返すため status を判定できる）
  const fetcher = useDemoFetcher();

  /**
   * ツアー完了状態を更新する。
   * update（部分更新）を先に試し、レコードが無い（404）ときのみ create する。
   * ネットワーク/サーバエラー時は破壊的な全置換 create を行わない。
   */
  const updateTourStatus = useCallback(
    async (completed: boolean): Promise<void> => {
      if (!userId) {
        return;
      }

      const { error: updateError } = await fetcher(
        "user_config",
        "userConfig",
        "updateUserConfig",
        {
          userId,
          requestBody: { onboarding_tour_completed: completed }
        }
      );

      if (updateError) {
        if (updateError?.status === 404) {
          // レコード未作成（新規ユーザー）のときのみ作成する
          const { error: createError } = await fetcher(
            "user_config",
            "userConfig",
            "createUserConfig",
            {
              requestBody: { user_id: userId, onboarding_tour_completed: completed }
            }
          );
          if (createError) {
            console.error("ツアー状態の作成に失敗:", createError);
            return;
          }
        } else {
          // ネットワーク/サーバエラー: 破壊的な全置換 create は行わない
          console.error("ツアー状態の更新に失敗:", updateError);
          return;
        }
      }

      // SWRのキャッシュを更新
      await mutate();
    },
    // fetcher は毎レンダー生成されるが状態を持たないため依存に含めない
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userId, mutate]
  );

  /**
   * ツアー状態をリセットする（動作確認用）。
   */
  const resetTourStatus = useCallback(async (): Promise<void> => {
    await updateTourStatus(false);
  }, [updateTourStatus]);

  // 完了判定:
  // - 取得成功 → DB値に従う
  // - 404（新規ユーザー） → 未完了扱い（ツアー表示）
  // - それ以外のエラー（ネットワーク等） → フェイルセーフで完了扱い（誤再表示を防ぐ）
  const tourCompleted = userConfig
    ? userConfig.onboarding_tour_completed ?? false
    : error
    ? error?.status !== 404
    : false;

  return {
    tourCompleted,
    isLoading: shouldFetch ? isLoading : false,
    error,
    updateTourStatus,
    resetTourStatus
  };
};
